export type StreetViewSessionState = 'idle' | 'checking' | 'available' | 'no-coverage' | 'error';

/**
 * A collateral pin can sit inside a plot or building, while imagery is captured
 * from the nearest road. Google recommends `NEAREST` searches remain at 1km or
 * less; 500m provides useful recovery without silently jumping to another area.
 */
export const STREET_VIEW_COVERAGE_RADIUS_METERS = 500;
export const STREET_VIEW_COVERAGE_PREFERENCE = 'nearest' as const;

export type StreetViewCoordinate = { lat: number; lng: number };

export function createStreetViewCoverageRequest(location: StreetViewCoordinate) {
  return {
    location,
    radius: STREET_VIEW_COVERAGE_RADIUS_METERS,
    preference: STREET_VIEW_COVERAGE_PREFERENCE,
  };
}

export function getStreetViewOffsetMeters(requested: StreetViewCoordinate, resolved: StreetViewCoordinate): number {
  const earthRadiusMeters = 6_371_000;
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const latDelta = toRadians(resolved.lat - requested.lat);
  const lngDelta = toRadians(resolved.lng - requested.lng);
  const latitudeFactor = Math.sin(latDelta / 2) ** 2
    + Math.cos(toRadians(requested.lat)) * Math.cos(toRadians(resolved.lat)) * Math.sin(lngDelta / 2) ** 2;
  return Math.round(2 * earthRadiusMeters * Math.atan2(Math.sqrt(latitudeFactor), Math.sqrt(1 - latitudeFactor)));
}

export function isValidStreetViewCoordinate(lat: number, lng: number): boolean {
  return Number.isFinite(lat)
    && Number.isFinite(lng)
    && lat >= -90
    && lat <= 90
    && lng >= -180
    && lng <= 180;
}

export function resolveStreetViewCoverageState(status: string): StreetViewSessionState {
  if (status === 'OK') return 'available';
  if (status === 'ZERO_RESULTS') return 'no-coverage';
  return 'error';
}

export function isCurrentStreetViewRequest(requestId: number, activeRequestId: number): boolean {
  return requestId === activeRequestId;
}

/**
 * Google Maps is a base-layer choice, not a Street View command. In an active
 * drawing session we keep the control surface open after choosing Google so
 * the explicit Street View action remains immediately available and the
 * polygon tool flow is not hidden beneath a second sheet open.
 */
export function shouldKeepMobileLayerSurfaceOpen(nextLayer: string, isDrawing: boolean): boolean {
  return isDrawing && nextLayer === 'google';
}
